import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, alpha } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { formatStringValue } from '../../utils/numberFormat';

interface ProfessionalKPICardProps {
  label: string;
  subLabel?: string;
  value: string | number;
  unit?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: string;
    direction: 'up' | 'down';
  };
  color?: string;
  loading?: boolean;
}

/** Normalize "CO2E" → "CO2e" in any display string */
const normalizeCO2e = (text: string): string => text.replaceAll('CO2E', 'CO2e');

const ProfessionalKPICard: React.FC<ProfessionalKPICardProps> = ({
  label: rawLabel,
  subLabel: rawSubLabel,
  value,
  unit: rawUnit,
  subtitle: rawSubtitle,
  icon,
  trend,
  color,
  loading = false,
}) => {
  const theme = useTheme();
  const primaryColor = color || theme.palette.primary.main;

  /** Uppercase text while preserving "CO2e" casing */
  const uppercasePreserveCO2e = (text: string): string =>
    text.replaceAll(/CO2E/gi, '\0').toUpperCase().replaceAll('\0', 'CO2e');

  const label = normalizeCO2e(rawLabel || '');
  const subLabel = rawSubLabel ? uppercasePreserveCO2e(normalizeCO2e(rawSubLabel)) : undefined;
  const unit = rawUnit ? normalizeCO2e(rawUnit) : undefined;
  const subtitle = rawSubtitle ? normalizeCO2e(rawSubtitle) : undefined;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${alpha(primaryColor, 0.15)}`,
        borderRadius: 3,
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        '&:hover': {
          transform: 'translateY(-6px) scale(1.01)',
          boxShadow: `0 20px 40px ${alpha(primaryColor, 0.2)}, 0 0 0 1px ${alpha(primaryColor, 0.1)}`,
          border: `1px solid ${alpha(primaryColor, 0.4)}`,
          background: 'rgba(255, 255, 255, 0.95)',
          '& .kpi-icon-box': {
            transform: 'rotate(5deg) scale(1.1)',
            boxShadow: `0 8px 16px ${alpha(primaryColor, 0.25)}`,
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${primaryColor}, ${alpha(primaryColor, 0.5)}, ${primaryColor})`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 3s ease-in-out infinite',
        },
        '@keyframes shimmer': {
          '0%, 100%': { backgroundPosition: '200% 0' },
          '50%': { backgroundPosition: '-200% 0' },
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Icon and Label Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          {icon && (
            <Box
              className="kpi-icon-box"
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(primaryColor, 0.1),
                color: primaryColor,
                transition: 'all 0.3s ease',
                '& svg': { fontSize: '1.25rem' },
                '& span': { fontSize: '1.25rem' } // for emoji strings
              }}
            >
              {typeof icon === 'string' ? <span>{icon}</span> : icon}
            </Box>
          )}
        </Box>

        {/* SubLabel */}
        {subLabel && (
          <Typography
            sx={{
              color: alpha(theme.palette.text.secondary, 0.7),
              fontWeight: 500,
              fontSize: '10px',
              mb: 1.5,
              letterSpacing: '0.5px'
            }}
          >
            {subLabel}
          </Typography>
        )}

        {/* Value */}
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '18px',
              color: theme.palette.text.primary,
              lineHeight: 1,
            }}
          >
            {formatStringValue(value)}
          </Typography>
          {unit && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '10px',
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>

        {/* Subtitle or Trend Footer */}
        {(subtitle || trend) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            {trend && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  background: trend.direction === 'up'
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.error.main, 0.1),
                }}
              >
                {trend.direction === 'up' ? (
                  <TrendingUp sx={{ fontSize: 14, color: theme.palette.success.main }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 14, color: theme.palette.error.main }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '11px',
                    color: trend.direction === 'up'
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  }}
                >
                  {trend.value}
                </Typography>
              </Box>
            )}
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfessionalKPICard;
